
import { useDurationOptions } from '@/hooks/useDurationOptions';

// Utilitaire pour les calculs de prix automatiques

export interface PriceCalculation {
  basePrice: number;
  quantity: number;
  totalPrice: number;
  calculation: string;
  priceUnit: string;
}

// Fonction pour convertir la durée en heures précises
export const parseHoursFromDuration = (duration: string, durationOptions?: any[]): number => {
  if (!duration) return 0;
  
  // Si c'est un nombre simple (ex: "3"), l'utiliser directement
  const numericValue = parseFloat(duration);
  if (!isNaN(numericValue) && numericValue > 0) {
    return numericValue;
  }
  
  // Si on a les options de durée, utiliser la table de référence
  if (durationOptions && durationOptions.length > 0) {
    const matchingOption = durationOptions.find(option => 
      option.label.toLowerCase() === duration.toLowerCase() ||
      duration.toLowerCase().includes(option.label.toLowerCase())
    );
    
    if (matchingOption) {
      return matchingOption.hours;
    }
  }
  
  // Fallback avec parsing manuel amélioré
  const lowerDuration = duration.toLowerCase();
  
  // Recherche de patterns spécifiques
  if (lowerDuration.includes('2 heures') || lowerDuration.includes('2h')) return 2;
  if (lowerDuration.includes('3 heures') || lowerDuration.includes('3h')) return 3;
  if (lowerDuration.includes('4 heures') || lowerDuration.includes('4h')) return 4;
  if (lowerDuration.includes('demi') || lowerDuration.includes('4-5')) return 4;
  if (lowerDuration.includes('journée') || lowerDuration.includes('jour')) return 8;
  if (lowerDuration.includes('2 jours')) return 16;
  if (lowerDuration.includes('week')) return 24;
  
  // Pattern générique pour extraire les heures
  const hourMatch = lowerDuration.match(/(\d+)\s*h/);
  if (hourMatch) {
    return parseInt(hourMatch[1]);
  }
  
  return 0; // Retourne 0 si aucune durée valide trouvée
};

export const calculateServicePrice = (
  basePrice: number,
  priceUnit: string,
  nbGuests: number | null,
  prestationDuration: string | null,
  manualQuantity?: number,
  isQuantifiable?: boolean,
  durationOptions?: any[]
): PriceCalculation => {
  const price = basePrice || 0;
  
  // Si une quantité manuelle est fournie, l'utiliser
  if (manualQuantity && manualQuantity > 0) {
    let totalPrice = price * manualQuantity;
    let calculation = `${price}€ × ${manualQuantity} = ${totalPrice.toLocaleString('fr-FR')}€`;
    
    // CORRECTION: Pour TOUS les services horaires (pas seulement quantifiables)
    if (priceUnit?.toLowerCase() === 'par_heure' && prestationDuration) {
      const durationInHours = parseHoursFromDuration(prestationDuration, durationOptions);
      if (durationInHours > 0) {
        totalPrice = price * durationInHours * manualQuantity;
        calculation = `${price}€/h × ${manualQuantity} × ${durationInHours}h = ${totalPrice.toLocaleString('fr-FR')}€`;
      }
    }
    // Pour les services par jour avec durée
    else if (priceUnit?.toLowerCase() === 'par_jour' && prestationDuration) {
      const durationInHours = parseHoursFromDuration(prestationDuration, durationOptions);
      const durationInDays = Math.ceil(durationInHours / 8); // 8h = 1 jour
      if (durationInDays > 1) {
        totalPrice = price * manualQuantity * durationInDays;
        calculation = `${price}€/jour × ${manualQuantity} × ${durationInDays}j = ${totalPrice.toLocaleString('fr-FR')}€`;
      }
    }
    // Pour les services par personne avec quantité manuelle spécifiée
    else if (priceUnit?.toLowerCase() === 'par_personne') {
      // Utiliser la quantité manuelle au lieu du nombre d'invités si elle est fournie
      totalPrice = price * manualQuantity;
      calculation = `${price}€ × ${manualQuantity} = ${totalPrice.toLocaleString('fr-FR')}€`;
    }
    
    return {
      basePrice: price,
      quantity: manualQuantity,
      totalPrice,
      calculation,
      priceUnit
    };
  }
  
  switch (priceUnit?.toLowerCase()) {
    case 'par_personne':
      if (nbGuests && nbGuests > 0) {
        const totalPrice = price * nbGuests;
        return {
          basePrice: price,
          quantity: nbGuests,
          totalPrice,
          calculation: `${price}€ × ${nbGuests} invités = ${totalPrice.toLocaleString('fr-FR')}€`,
          priceUnit
        };
      }
      return {
        basePrice: price,
        quantity: 1,
        totalPrice: price,
        calculation: `${price}€ / par personne`,
        priceUnit
      };
      
    case 'par_heure':
      if (prestationDuration) {
        const durationInHours = parseHoursFromDuration(prestationDuration, durationOptions);
        if (durationInHours > 0) {
          const totalPrice = price * durationInHours;
          return {
            basePrice: price,
            quantity: durationInHours,
            totalPrice,
            calculation: `${price}€/h × ${durationInHours}h = ${totalPrice.toLocaleString('fr-FR')}€`,
            priceUnit
          };
        }
      }
      return {
        basePrice: price,
        quantity: 1,
        totalPrice: price,
        calculation: `${price}€ / par heure`,
        priceUnit
      };
      
    case 'par_jour':
      return {
        basePrice: price,
        quantity: 1,
        totalPrice: price,
        calculation: `${price.toLocaleString('fr-FR')}€ / forfait`,
        priceUnit
      };
      
    default:
      return {
        basePrice: price,
        quantity: 1,
        totalPrice: price,
        calculation: `${price.toLocaleString('fr-FR')}€`,
        priceUnit: 'par_prestation'
      };
  }
};

export const getPriceUnitDisplayText = (priceUnit: string): string => {
  switch (priceUnit?.toLowerCase()) {
    case 'par_personne': return 'par personne';
    case 'par_heure': return 'par heure';
    case 'par_jour': return 'par jour';
    case 'par_prestation': return 'forfait';
    default: return 'forfait';
  }
};
